import { Head, Link, router } from "@inertiajs/react"

import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem } from "@/types"

interface Customer {
  id: number
  display_name: string
}

interface Location {
  id: number
  name: string | null
  display_name: string
  address: string | null
  city: string | null
  state: string | null
  zip: string | null
  phone: string | null
  email: string | null
  status: number
}

interface Props {
  customer: Customer
  locations: Location[]
}

export default function Index({ customer, locations }: Props) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Customers", href: "/customers" },
    { title: customer.display_name, href: `/customers/${customer.id}` },
    { title: "Locations", href: `/customers/${customer.id}/locations` },
  ]

  const handleDelete = (id: number) => {
    if (confirm("Are you sure you want to delete this location?")) {
      router.delete(`/customers/${customer.id}/locations/${id}`)
    }
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`Locations - ${customer.display_name}`} />

      <div className="p-6">
        <div className="mb-4 flex items-center justify-between">
          <h1 className="text-2xl font-semibold">
            Locations for {customer.display_name}
          </h1>
          <Link
            href={`/customers/${customer.id}/locations/new`}
            className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-2 text-white"
          >
            Add Location
          </Link>
        </div>

        {locations.length > 0 ? (
          <table className="border-hpi-light-gray w-full border-collapse border">
            <thead>
              <tr className="bg-hpi-pale-gray">
                <th className="border-hpi-light-gray border px-4 py-2 text-left">
                  Name
                </th>
                <th className="border-hpi-light-gray border px-4 py-2 text-left">
                  Address
                </th>
                <th className="border-hpi-light-gray border px-4 py-2 text-left">
                  Phone
                </th>
                <th className="border-hpi-light-gray border px-4 py-2 text-left">
                  Email
                </th>
                <th className="border-hpi-light-gray border px-4 py-2 text-left">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {locations.map((location) => (
                <tr key={location.id} className="hover:bg-hpi-pale-blue">
                  <td className="border-hpi-light-gray border px-4 py-2">
                    <Link
                      href={`/customers/${customer.id}/locations/${location.id}`}
                      className="text-hpi-active-blue hover:underline"
                    >
                      {location.display_name}
                    </Link>
                  </td>
                  <td className="border-hpi-light-gray border px-4 py-2">
                    {[location.city, location.state].filter(Boolean).join(", ")}
                  </td>
                  <td className="border-hpi-light-gray border px-4 py-2">
                    {location.phone}
                  </td>
                  <td className="border-hpi-light-gray border px-4 py-2">
                    {location.email}
                  </td>
                  <td className="border-hpi-light-gray border px-4 py-2">
                    <Link
                      href={`/customers/${customer.id}/locations/${location.id}/edit`}
                      className="text-hpi-active-blue hover:text-hpi-active-blue mr-2"
                    >
                      Edit
                    </Link>
                    <button
                      onClick={() => handleDelete(location.id)}
                      className="text-hpi-red hover:underline"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="text-hpi-med-gray">No locations found.</p>
        )}

        <div className="mt-4">
          <Link
            href={`/customers/${customer.id}`}
            className="text-hpi-active-blue hover:text-hpi-active-blue"
          >
            Back to Customer
          </Link>
        </div>
      </div>
    </AppLayout>
  )
}
